import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Activity, AlertTriangle, Shield, Database, TrendingUp, Filter, Download, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { systemAuditService } from '../../services/systemAuditService';
import { auditService } from '../../services/auditService';
import AuditStreamPanel from './components/AuditStreamPanel';
import RlsPolicyPanel from './components/RlsPolicyPanel';
import AlertsPanel from './components/AlertsPanel';
import FilterControls from './components/FilterControls';
import Icon from '../../components/AppIcon';


export default function SystemAuditDashboard() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAuditLogs: 0,
    totalErrors: 0,
    criticalErrors: 0,
    rlsPolicyDenials: 0,
    activeAlerts: 0,
    errorRatePercentage: 0
  });
  const [auditLogs, setAuditLogs] = useState([]);
  const [rlsLogs, setRlsLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('audit-stream');
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
    endDate: new Date()?.toISOString(),
    actionType: [],
    severity: [],
    decision: null
  });

  useEffect(() => {
    loadDashboardData();
    setupRealtimeSubscriptions();
  }, [filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load stats
      const statsData = await systemAuditService?.getDashboardStats(
        profile?.tenant_id,
        filters?.startDate,
        filters?.endDate
      );
      setStats(statsData);

      // Load audit logs
      const auditData = await auditService?.getAuditLogs({
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        actionType: filters?.actionType,
        limit: 50
      });
      setAuditLogs(auditData);

      // Load RLS policy logs
      const rlsData = await systemAuditService?.getRlsPolicyLogs({
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        decision: filters?.decision,
        limit: 50
      });
      setRlsLogs(rlsData);

      // Load alerts
      const alertsData = await systemAuditService?.getSuperAdminAlerts({
        status: 'active',
        severity: filters?.severity,
        limit: 20
      });
      setAlerts(alertsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    const channel = supabase?.channel('audit-dashboard-changes')?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'audit_logs' },
        () => {
          loadDashboardData();
        }
      )?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'system_errors' },
        () => {
          loadDashboardData();
        }
      )?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'rls_policy_logs' },
        () => {
          loadDashboardData();
        }
      )?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'super_admin_alerts' },
        () => {
          loadDashboardData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  const handleExport = async () => {
    try {
      const csvContent = await auditService?.exportAuditLogs(filters);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date()?.toISOString()}.csv`;
      a?.click();
      window.URL?.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting audit logs:', error);
    }
  };

  const StatCard = ({ title, value, icon: Icon, bgColor, trend }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
          {trend && (
            <p className="text-xs text-gray-500 mt-2">{trend}</p>
          )}
        </div>
        <div className={`${bgColor} p-3 rounded-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Helmet>
        <title>System Audit Dashboard - Real-Time Monitoring</title>
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">System Audit Dashboard</h1>
              <p className="text-gray-600">Real-time monitoring of RLS policies, user actions, and system events</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={loadDashboardData}
                className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatCard
            title="Total Audit Logs"
            value={stats?.totalAuditLogs}
            icon={Activity}
            bgColor="bg-blue-600"
            trend="Last 7 days"
          />
          <StatCard
            title="Critical Errors"
            value={stats?.criticalErrors}
            icon={AlertTriangle}
            bgColor="bg-red-600"
            trend={`${stats?.errorRatePercentage}% error rate`}
          />
          <StatCard
            title="RLS Policy Denials"
            value={stats?.rlsPolicyDenials}
            icon={Shield}
            bgColor="bg-orange-600"
            trend="Security violations"
          />
          <StatCard
            title="Active Alerts"
            value={stats?.activeAlerts}
            icon={AlertTriangle}
            bgColor="bg-yellow-600"
            trend="Requires attention"
          />
          <StatCard
            title="Database Sync Events"
            value={stats?.totalAuditLogs}
            icon={Database}
            bgColor="bg-green-600"
            trend="Real-time tracking"
          />
          <StatCard
            title="System Performance"
            value="98.5%"
            icon={TrendingUp}
            bgColor="bg-purple-600"
            trend="Optimal"
          />
        </div>

        {/* Filter Controls */}
        <FilterControls filters={filters} onFiltersChange={setFilters} isLoading={loading} />

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('audit-stream')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'audit-stream' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Audit Stream
              </button>
              <button
                onClick={() => setActiveTab('rls-policies')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'rls-policies' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                RLS Policy Decisions
              </button>
              <button
                onClick={() => setActiveTab('alerts')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'alerts' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Critical Alerts
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'audit-stream' && <AuditStreamPanel logs={auditLogs} />}
            {activeTab === 'rls-policies' && <RlsPolicyPanel logs={rlsLogs} />}
            {activeTab === 'alerts' && <AlertsPanel alerts={alerts} onRefresh={loadDashboardData} />}
          </div>
        </div>
      </div>
    </div>
  );
}